import Navbar from '../components/Navbar'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const TOC = [
  { id: 'what-are-cookies',  label: 'What are cookies?' },
  { id: 'what-we-use',       label: 'Cookies we use' },
  { id: 'your-choices',      label: 'Your choices' },
  { id: 'third-party',       label: 'Third-party cookies' },
  { id: 'contact',           label: 'Contact' },
]

const COOKIES = [
  {
    name: 'verse_cookie_consent',
    type: 'Necessary',
    purpose: 'Stores your cookie consent preferences so we don\'t ask again on every visit.',
    duration: '1 year',
  },
  {
    name: 'sb-access-token',
    type: 'Necessary',
    purpose: 'Keeps you logged into your Verse account during your browser session.',
    duration: 'Session (until you close the browser or log out)',
  },
  {
    name: 'sb-refresh-token',
    type: 'Necessary',
    purpose: 'Renews your login token so you stay logged in without entering your password again.',
    duration: '7 days',
  },
]

const TYPE_COLORS = {
  Necessary: 'var(--teal)',
  Analytics: 'var(--purple)',
  Marketing: 'var(--pink)',
}

export default function CookiePolicyPage() {
  return (
    <div className="legal-page">
      <SEO title="Cookie Policy" description="How Verse uses cookies and similar technologies." />
      <Navbar />
      <div className="legal-page__inner">
        <header className="legal-page__header">
          <p className="legal-page__label">Legal</p>
          <h1 className="legal-h1">Cookie Policy</h1>
          <p className="legal-page__meta">Last updated: March 2026</p>
        </header>

        <nav className="legal-toc" aria-label="Table of contents">
          <p className="legal-toc__heading">On this page</p>
          <ol className="legal-toc__list">
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="legal-toc__link">{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="what-are-cookies" className="legal-section">
          <h2 className="legal-h2">What are cookies?</h2>
          <p>
            Cookies are small text files that websites store in your browser. They're used to remember things like whether you're logged in, your preferences, or how you use a site.
          </p>
          <p>
            Cookies are not programs — they can't run code or install anything on your computer. They're just data files that your browser reads and sends back to a website when you visit.
          </p>
          <p>
            There are a few types:
          </p>
          <ul className="legal-list">
            <li><strong>Session cookies</strong> are temporary and deleted when you close your browser.</li>
            <li><strong>Persistent cookies</strong> stay on your device for a set amount of time (like 1 year).</li>
            <li><strong>First-party cookies</strong> are set by the site you're visiting (that's us).</li>
            <li><strong>Third-party cookies</strong> are set by other services we use (like our payment processor).</li>
          </ul>
        </section>

        <section id="what-we-use" className="legal-section">
          <h2 className="legal-h2">Cookies we use</h2>
          <p>Here's a complete list of the cookies Verse uses:</p>

          <div className="legal-table-wrap">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Cookie name</th>
                  <th>Type</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {COOKIES.map((cookie) => (
                  <tr key={cookie.name}>
                    <td><code className="legal-code">{cookie.name}</code></td>
                    <td>
                      <span className="legal-badge" style={{ color: TYPE_COLORS[cookie.type], background: `${TYPE_COLORS[cookie.type]}18` }}>
                        {cookie.type}
                      </span>
                    </td>
                    <td>{cookie.purpose}</td>
                    <td>{cookie.duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="legal-callout" style={{ marginTop: '1.5rem' }}>
            <strong>Necessary cookies</strong> cannot be disabled — they are essential for the site to function. You can opt out of Analytics and Marketing cookies at any time.
          </div>
        </section>

        <section id="your-choices" className="legal-section">
          <h2 className="legal-h2">Your choices</h2>
          <p>
            You are in control of which optional cookies are set on your device. You have several ways to manage them:
          </p>
          <ul className="legal-list">
            <li>
              <strong>Cookie preferences on Verse:</strong> Visit your <Link to="/privacy-settings" className="legal-link">privacy settings</Link> to change which cookies you allow. You can update your choices at any time.
            </li>
            <li>
              <strong>Browser settings:</strong> Most browsers let you block or delete cookies through their settings. Note that disabling necessary cookies will break key features like logging in.
            </li>
          </ul>
          <p>
            When you first visit Verse, you'll see a cookie banner where you can accept all, reject all, or customise your preferences. Your choice is remembered for 1 year via the <code className="legal-code">verse_cookie_consent</code> cookie.
          </p>
        </section>

        <section id="third-party" className="legal-section">
          <h2 className="legal-h2">Third-party cookies</h2>
          <p>Some cookies are set by services we use to run and improve Verse:</p>
          <ul className="legal-list">
            <li>
              <strong>Supabase</strong> — Our database and authentication provider sets cookies to manage your login session (<code className="legal-code">sb-access-token</code> and <code className="legal-code">sb-refresh-token</code>). These are necessary cookies and cannot be disabled. <a href="https://supabase.com/privacy" className="legal-link" target="_blank" rel="noopener noreferrer">Supabase Privacy Policy</a>.
            </li>
          </ul>
          <p>
            We do not use any marketing, advertising, or third-party tracking cookies. Verse will never track you for ad targeting.
          </p>
        </section>

        <section id="contact" className="legal-section">
          <h2 className="legal-h2">Contact</h2>
          <p>
            If you have questions about how we use cookies, email us at{' '}
            <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.
          </p>
        </section>
      </div>
    </div>
  )
}
